// Gear — the admin door of the top bar, ONE rule for every bar (the Navbar, the
// slim bar of the project-less pages), ruled with ADR-080 §12
// (FRONTEND-PATTERNS §Navigation / shell):
//   • anyone with an admin section — one click into the project's first allowed
//     section (`firstAllowedAdminRoute`), lit on an admin page: unchanged;
//   • a sysadmin INSIDE a project — a two-row menu: Project administration (the
//     same landing) · Platform settings — two levels behind one door, the
//     Atlassian shape; the menu is a sheet on a phone like every menu;
//   • a sysadmin OUTSIDE a project — the platform pages, the project chooser,
//     the profile page — a plain link to Platform settings, lit on the platform.
// The phone's menu drawer carries the same row (MobileNavDrawer). Nothing here
// for a member without an admin section who is not a sysadmin. The registry
// (`config/nav.ts`) owns the doors; this piece only draws them.

import type { ReactNode } from 'react';
import { Link as RouterLink, useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { icons } from '../ui/icons.js';
import { IconButton } from '../ui/IconButton.js';
import { Menu, MenuItem } from '../ui/Menu.js';
import { useMe } from '../../hooks/useMe.js';
import { useCan } from '../../hooks/useCan.js';
import { useEntitlements } from '../../hooks/useEntitlements.js';
import { PLATFORM_HOME, firstAllowedAdminRoute, gearVisible, navItemPath, visibleNavItems } from '../../config/nav.js';

// No display utility here — the bar sets it through `className` (e.g. `hidden md:inline-flex`)
// on the wrapper, so a conflicting unconditional `inline-flex` can't override a `hidden`.
const linkClass = 'inline-flex items-center justify-center p-2 min-w-[2.5rem] min-h-[2.5rem] rounded-md outline-none focus-ring transition-colors';
const LIT = 'text-[var(--color-primary-display)] bg-[var(--color-primary-light)]';
const QUIET = 'text-[var(--color-text-secondary)] hover:bg-[var(--color-bg-hover)] hover:text-[var(--color-text-primary)]';

export function Gear({ projectId, className }: { projectId: string | null; className?: string }) {
  const { t } = useTranslation('nav');
  const location = useLocation();
  const isSysadmin = useMe((m) => m.user.is_sysadmin).data === true;
  const can = useCan(projectId ?? '');
  const entitlements = useEntitlements(projectId ?? '');
  const ctx = { can, entitlements };
  const isPlatform = location.pathname.startsWith('/platform');
  const firstAdmin = projectId != null && gearVisible(ctx) ? firstAllowedAdminRoute(ctx) : null;
  const adminActive = projectId != null && visibleNavItems('admin', ctx).some((i) => location.pathname.startsWith(navItemPath(projectId, i.route)));
  const glyph = (on: boolean) => <icons.settings aria-hidden size={20} weight={on ? 'fill' : 'regular'} />;
  const seat = (node: ReactNode) => (className ? <span className={`${className} shrink-0`}>{node}</span> : node);

  // A sysadmin inside a project: the two doors behind one gear.
  if (isSysadmin && projectId != null && firstAdmin !== null) {
    return seat(
      <Menu
        label={t('admin.title')}
        placement="bottom end"
        renderTrigger={() => (
          <IconButton aria-label={t('admin.title')} className={adminActive ? LIT : ''}>
            {glyph(adminActive)}
          </IconButton>
        )}
      >
        <MenuItem href={navItemPath(projectId, firstAdmin)} icon={<icons.adjust aria-hidden size={18} />}>
          {t('gear.label')}
        </MenuItem>
        <MenuItem href={PLATFORM_HOME} icon={<icons.building aria-hidden size={18} />}>
          {t('gear.platform')}
        </MenuItem>
      </Menu>,
    );
  }
  // A sysadmin outside a project: straight to Platform settings.
  if (isSysadmin && projectId == null) {
    return seat(
      <RouterLink to={PLATFORM_HOME} aria-label={t('gear.platform')} aria-current={isPlatform ? 'page' : undefined} className={`${linkClass} ${isPlatform ? LIT : QUIET}`}>
        {glyph(isPlatform)}
      </RouterLink>,
    );
  }
  // Everyone else: one click into the project's admin — or no door at all.
  if (projectId == null || firstAdmin === null) return null;
  return seat(
    <RouterLink to={navItemPath(projectId, firstAdmin)} aria-label={t('gear.label')} aria-current={adminActive ? 'page' : undefined} className={`${linkClass} ${adminActive ? LIT : QUIET}`}>
      {glyph(adminActive)}
    </RouterLink>,
  );
}
